import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { setAccessToken } from './auth/tokenStore';
import { HomePage } from './pages/HomePage';
import { GalleryPage } from './pages/GalleryPage';
import { CreatePage } from './pages/CreatePage';
import { EditorPage } from './pages/EditorPage';
import { AboutPage } from './pages/AboutPage';

// Shows the user's name + sign-out when authenticated, or a sign-in button otherwise.
function AuthButton() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <span className="text-gray-400 text-sm">…</span>;
  }

  if (auth.isAuthenticated) {
    const name = auth.user?.profile.given_name || auth.user?.profile.name || auth.user?.profile.email;
    return (
      <div className="flex items-center space-x-3">
        <span className="hidden sm:inline text-gray-600 text-sm">Hi, {name}</span>
        <button
          onClick={() => auth.signoutRedirect()}
          className="text-gray-600 hover:text-indigo-600 font-medium transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => auth.signinRedirect()}
      className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all"
    >
      Sign in
    </button>
  );
}

// Rendered at /callback while the OIDC redirect is being processed.
function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Once react-oidc-context has finished the code->token exchange, hand control back
  // to react-router so the SPA leaves /callback and the query string is dropped.
  useEffect(() => {
    if (!auth.isLoading && (auth.isAuthenticated || auth.error)) {
      navigate('/', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent mx-auto mb-4" />
        <p className="text-gray-600">{auth.error ? `Sign-in error: ${auth.error.message}` : 'Signing you in…'}</p>
      </div>
    </div>
  );
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [triedSilent, setTriedSilent] = useState(false);
  const auth = useAuth();

  // Keep the axios token store in sync with the current session so API calls
  // carry the bearer token. Runs whenever the auth user changes.
  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null);
  }, [auth.user]);

  // Zero-click SSO: on first load, silently ask the ID server (prompt=none in a hidden
  // iframe) whether there's an existing session. If yes, we sign in with no interaction;
  // if not, the call fails quietly and the user stays logged out (login is optional).
  // Only same-site origins (e.g. *.local.letech.solutions) can pass the cookie here.
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || triedSilent) return;
    const path = window.location.pathname;
    if (path.startsWith('/callback') || path.startsWith('/silent-renew')) return;
    setTriedSilent(true);
    auth.signinSilent().catch(() => { /* no existing session — stay anonymous */ });
  }, [auth, auth.isLoading, auth.isAuthenticated, triedSilent]);

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <Routes>
          {/* OIDC sign-in landing. react-oidc-context processes the code/state on load
              and onSigninCallback sends the user back to "/", so this is just a spinner. */}
          <Route path="/callback" element={<AuthCallback />} />
          {/* Hidden iframe target for silent token renewal. */}
          <Route path="/silent-renew" element={<div />} />

          {/* Editor page has its own layout */}
          <Route path="/editor/:id" element={<EditorPage />} />

          {/* All other pages use the main layout */}
          <Route
            path="*"
            element={
              <>
                <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                      <div className="flex items-center">
                        <Link to="/" className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                            </svg>
                          </div>
                          <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">ColorCraft</span>
                        </Link>
                      </div>
                      <div className="hidden md:flex items-center space-x-8">
                        <Link to="/" className="text-gray-600 hover:text-indigo-600 font-medium transition-colors">Home</Link>
                        <Link to="/gallery" className="text-gray-600 hover:text-indigo-600 font-medium transition-colors">Gallery</Link>
                        <Link to="/create" className="text-gray-600 hover:text-indigo-600 font-medium transition-colors">Create</Link>
                        <Link to="/about" className="text-gray-600 hover:text-indigo-600 font-medium transition-colors">About</Link>
                        <Link
                          to="/create"
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all"
                        >
                          Upload Photo
                        </Link>
                        <AuthButton />
                      </div>
                      <div className="md:hidden flex items-center">
                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 p-2">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  {mobileMenuOpen && (
                    <div className="md:hidden bg-white border-t">
                      <div className="px-4 py-3 space-y-3">
                        <Link to="/" className="block text-gray-600 hover:text-indigo-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                        <Link to="/gallery" className="block text-gray-600 hover:text-indigo-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Gallery</Link>
                        <Link to="/create" className="block text-gray-600 hover:text-indigo-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Create</Link>
                        <Link to="/about" className="block text-gray-600 hover:text-indigo-600 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
                      </div>
                    </div>
                  )}
                </nav>

                <main>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/gallery" element={<GalleryPage />} />
                    <Route path="/create" element={<CreatePage />} />
                    <Route path="/about" element={<AboutPage />} />
                  </Routes>
                </main>

                <footer className="bg-gray-900 text-gray-400 py-8 mt-20">
                  <div className="max-w-7xl mx-auto px-4 text-center">
                    <p>&copy; {new Date().getFullYear()} ColorCraft. All rights reserved.</p>
                  </div>
                </footer>
              </>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
